import { PlusIcon } from "lucide-react";
import { useTitle } from "react-use";

import {
  CommandMenu,
  CommandMenuProvider,
  useCommandMenu,
} from "@/components/CommandMenu";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { CommandGroup, CommandItem } from "@/components/ui/command";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { useNavSelected } from "@/utils/useNavStack";
import { NewTestRunButton } from "../components/NewTestRunButton";
import { newTestRunDialogState } from "../components/store";
import {
  TestRunFilterPopover,
  TestRunFilters,
} from "../components/TestRunFilters";
import {
  TestRunListItem,
  TestRunLoadingItem,
} from "../components/TestRunListItem";
import { TestRunSearch } from "../components/TestRunSearch";
import type { TestRunNew } from "../types";

export function TestRunsPage({
  runs,
  isPending,
}: {
  runs: TestRunNew[];
  isPending: boolean;
}) {
  useTitle("Test Runs");

  const { selected } = useNavSelected();

  useTinyKeys({
    ArrowDown: (e) => {
      const grid = document.querySelector(
        "div[role='grid']",
      ) as HTMLElement | null;

      if (grid && !grid.contains(document.activeElement)) {
        e.stopPropagation();
        e.preventDefault();
        grid.focus();
      }
    },
  });

  return (
    <>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb>Test runs</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
        <SubHeader.Right className="gap-2">
          <TestRunSearch />
          <TestRunFilterPopover runs={runs} />
          <NewTestRunButton />
        </SubHeader.Right>
      </SubHeader>
      <TestRunFilters runs={runs} />
      <div className="h-full w-full overflow-auto">
        <NavList
          items={runs}
          aria-label="Test runs"
          initialIndex={runs.findIndex((run) => run.id === selected)}
          isLoading={isPending}
          LoadingComponent={TestRunLoadingItem}
          loadingItems={30}
          autoFocus
        >
          {(run) => (
            <NavListItem asChild key={run.id}>
              <TestRunListItem run={run} />
            </NavListItem>
          )}
        </NavList>
      </div>
      <CommandMenuProvider>
        <CommandMenu>
          <TestRunsCommands />
        </CommandMenu>
      </CommandMenuProvider>
    </>
  );
}

function TestRunsCommands() {
  const { setOpen, page } = useCommandMenu();
  if (page) return null;
  return (
    <CommandGroup heading="Test runs">
      <CommandItem
        onSelect={() => {
          newTestRunDialogState.show = true;
          setOpen(false);
        }}
      >
        <PlusIcon />
        New test run
      </CommandItem>
    </CommandGroup>
  );
}
